// import/no-extraneous-dependencies
/* eslint-disable camelcase */
/* eslint jsx-a11y/label-has-associated-control: ["error", { assert: "either", depth: 25 }] */
import React, { useEffect, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import Datepicker from 'react-tailwindcss-datepicker';
import 'react-dropdown/style.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faCalendarAlt, faPlane, faFileAlt, faDownload, faChartLine, faClock, faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';
import { useGetSafetyLogsQuery } from '../../../redux/apiSlice';
import Pagination from '../../components/Pagination/Pagination';
// import '../components/Pagination/style.css';
import LinkButton from '../../components/Buttons/LinkButton';

import ExportButtons from '../../components/CreatePdfExcel/ExportButtons';

const PageSize = 10;
const SafetyLogs = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [category, setCategory] = useState('');
  const [cause, setCause] = useState('');
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [value, setValue] = useState({
    startDate: null,
    endDate: null,
  });

  const { data: logs, error, isFetching } = useGetSafetyLogsQuery();

  useEffect(() => {
    if (logs) {
      setFilteredLogs(logs);
    }
  }, [logs]);

  const handleDateValueChange = (newValue) => {
    setValue(newValue);
  };

  const handleFilterChange = (filterType, selectedValue) => {
    if (filterType === 'category') {
      setCategory(selectedValue);
    } else if (filterType === 'cause') {
      setCause(selectedValue);
    }
  };

  const debouncedSearch = debounce((query) => {
    setSearchQuery(query);
  }, 300);

  const handleSearch = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  useEffect(() => {
    let filtered = logs || [];

    if (category) {
      filtered = filtered.filter((log) => log.category === category);
    }
    if (cause) {
      filtered = filtered.filter((log) => log.cause === cause);
    }
    if (searchQuery) {
      // eslint-disable-next-line max-len
      filtered = filtered.filter((log) => log.event_description?.toLowerCase().includes(searchQuery.toLowerCase()));
    }
    if (value.startDate && value.endDate) {
      const startDate = new Date(value.startDate);
      const endDate = new Date(value.endDate);
      filtered = filtered.filter((log) => {
        const logDate = new Date(log.date);
        return logDate >= startDate && logDate <= endDate;
      });
    }

    setFilteredLogs(filtered);
    setCurrentPage(1); // Reset to first page whenever filters change
  }, [logs, category, cause, searchQuery, value]);

  if (error) {
    // Error handling for logs fetching
  }

  if (!logs && !isFetching) {
    return (
      <div className="bg-gray-200 text-red-700 bg-opacity-90 lg:p-16 p-8 fixed z-50 w-full h-full top-0 left-0 flex flex-col items-center justify-center">
        <h1>Error while fetching logs, please try again later!</h1>
      </div>
    );
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const paginatedLogs = useMemo(() => {
    const firstPageIndex = (currentPage - 1) * PageSize;
    const lastPageIndex = firstPageIndex + PageSize;
    return filteredLogs.slice(firstPageIndex, lastPageIndex);
  }, [currentPage, filteredLogs]);

  // Calculate statistics
  const totalLogs = logs?.length || 0;
  const thisMonthLogs = logs?.filter((log) => {
    const logDate = new Date(log.date);
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    return logDate.getMonth() === currentMonth && logDate.getFullYear() === currentYear;
  }).length || 0;
  const thisYearLogs = logs?.filter((log) => {
    const logDate = new Date(log.date);
    const currentYear = new Date().getFullYear();
    return logDate.getFullYear() === currentYear;
  }).length || 0;

  // Get top categories
  const categoryCounts = logs?.reduce((acc, log) => {
    acc[log.category] = (acc[log.category] || 0) + 1;
    return acc;
  }, {}) || {};
  const topCategories = Object.entries(categoryCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  const causeOptions = [
    'ATC error /violation',
    'Pilot error /violati',
    'Techinical',
    'Weather',
    'SERVICING',
    'AIRCRAFT ON THE RWY',
    'CLOUDS',
    'Procedures',
    'Fuel',
    'Cause at Destination',
    'Others',
    'Equipment Failure',
    'RWY & TWY Lights US',
    'FOD on RWY',
    'OTHER ACFT',
    'OBSTACLE',
    'COMPONENT FAILURE',
    'TCAS ALERT',
    'LOSS OF SEPARATION',
    'VEHICLE ON RWY',
    'JET BLAST',
    'ANIMALS(NOT BIRDS)',
    'INTERFERENCE',
    'UNSTABLE APPROACH',
    'Bird Strike',
  ];

  const categoryOptions = [
    'missed approach/go round',
    'unstable approach',
    'delay',
    'airspace voilation',
    'Diversion',
    'Runway incursion (ACFT, VEHICLE OR PERSON, and ANIMAL- RI )',
    'Airprox ACAS ALERT/LOSS OF SEPARATION/NEAR OR MIDAIR COLLISION (MAC)',
    'Bird strikes (BIRDS)',
    'VVIP',
    'Cancelation of flight',
    'EQPT, NAV AIDS , ATC AND CNS Facility Failure/Malfunctioning (ATM/CNS)',
    'Emergency',
    'Aborted take-off',
    'Radio comm failure',
    'Tyre burst',
    'Did not comply with ATC/Instruction',
    'No estimates passed to ATC',
    'Airside surfaces',
    'General',
    'AERODROME (ADRM)',
    'ACCIDENT/INCIDENT (ADREP)',
    'ABRUPT MANEUVRE (AMAN)',
    'ABNORMAL RUNWAY CONTACT (ARC)',
    'ATC',
    'UNINTENDED FLIGHT INTO IMC CONDITIONS (IMC) BY VFR FLIGHTS',
    'CABIN SAFETY EVENTS (CABIN)',
    'CONTROLLED FLIGHT INTO TERRAIN (CFIT)',
    'COLLISION WITH OBSTACLES DURING TAKEOFF AND LANDING (CTOL)',
    'EVACUATION - PERSONS INJURED DURING EVACUATION (EVAC)',
    'FIRE/SMOKE WITHOUT IMPACT (F-NI)',
    'RUNWAY EXCURSION VEER OFF RUNWAY DURING TAKEOFF OR LANDING (RE)',
    'LOSS OF CONTROL OR DEVIATION - IN FLIGHT (LOC -I)',
    'Accumulation of snow, ice, freezing rain or frost (ICE)',
    'FIRE/SMOKE-POST IMPACT (F-POST)',
    'R2',
    'Weather',
    'R1',
    'SECURITY RELATED HIJACK/UNRULLY PAX/ INTERFERENCE (SEC)',
    'SYSTEM/COMPONENT FAILURE NON POWER PLANT(SCF-NP)',
    'POWER PLANT FAILURE OF AN ACT (SCF-PP)',
    'GROUND HANDLING- JET BLAST/ COLLISIONS DURING BOARDING, LOADING, SERVICING (RAMP)',
    'TURBULENCE INFLIGHT (TURB)',
    'TOUCHDOWN OFF RUNWAY SURFACE (USOS-UNDERSHOOT/OVERSHOOT)',
    'LANDING ENROUTE DUE TO LOSS OF LIFTING (LOLI)',
    'FLIGHT INTO WINDSHEAR OR THURDERSTORM/HEAVY RAIN (WSTRW)',
    'COLLISION WHILE TAXING- NOT ON RUNWAY IN USE (GCOL)',
    'COLLISION/NEAR COLLISION WITH OBSTACLES/TERRAIN/OBJECTS WHILE INTENTIONALLY OPERATING NEAR THE SURFACE (LALT)',
    'GLIDER TOWING RELATED EVENTS (GTOW)',
    'UNKNOWN OR UNDETERMINED - NIL INFORMATION, MISSING AFT (UNK)',
    'FUEL RELATED - REDUCED FUEL (FUEL)',
  ];

  // Define columns for export
  const columns = [
    { headerName: 'Date', field: 'date' },
    { headerName: 'Time', field: 'time' },
    { headerName: 'Aircraft Callsign', field: 'aircraft_callsign' },
    { headerName: 'Aircraft Type', field: 'aircraft_type' },
    { headerName: 'Aircraft Registration', field: 'aircraft_registration' },
    { headerName: 'Operator', field: 'operator' },
    { headerName: 'Route', field: 'route' },
    { headerName: 'Event Description', field: 'event_description' },
    { headerName: 'Category', field: 'category' },
    { headerName: 'Cause', field: 'cause' },
  ];

  return (
    <>
      <section className="p-8 w-full text-lg dark:bg-gray-900 p-3 sm:p-5 antialiased flex gap-6">
        <div className="bg-private p-5 dark:bg-gray-800 relative shadow-md rounded overflow-hidden flex-1">
          {/* Enhanced Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-600 rounded-lg">
                <FontAwesomeIcon icon={faFileAlt} className="text-white text-lg" />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-white">Safety Logs Management</h1>
                <p className="text-gray-300 text-sm">Monitor and manage safety events and incidents</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <LinkButton path="/private/safetylogs-form" linktext="Report New Log" />
              <ExportButtons
                data={filteredLogs}
                columns={columns}
                fileName="safety_logs"
              />
            </div>
          </div>

          {/* Enhanced Search and Filters */}
          <div className="bg-gray-750 rounded-lg p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Enhanced Search */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search logs..."
                  value={searchQuery}
                  onChange={handleSearch}
                  className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-white placeholder-gray-400"
                />
              </div>

              {/* Enhanced Date Picker */}
              <div className="relative">
                <FontAwesomeIcon
                  icon={faCalendarAlt}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10"
                />
                <Datepicker
                  inputClassName="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-white placeholder-gray-400"
                  showFooter
                  separator="to"
                  value={value}
                  onChange={handleDateValueChange}
                  containerClassName="relative"
                  placeholder="Filter by date range"
                />
              </div>

              {/* Enhanced Category Filter */}
              <select
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-white"
                value={category}
                onChange={(event) => handleFilterChange('category', event.target.value)}
              >
                <option value="">All Categories</option>
                {categoryOptions.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>

              {/* Enhanced Cause Filter */}
              <select
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 text-white"
                value={cause}
                onChange={(event) => handleFilterChange('cause', event.target.value)}
              >
                <option value="">All Causes</option>
                {causeOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Results Count */}
            <div className="mt-4 text-sm text-gray-300">
              {filteredLogs.length}
              {' '}
              log
              {filteredLogs.length !== 1 ? 's' : ''}
              {' '}
              found
            </div>
          </div>
          {/* Enhanced Table */}
          {filteredLogs && filteredLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-750">
                  <tr className="border-b border-gray-600">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Aircraft Info
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Event Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Cause
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {paginatedLogs.map(({
                    id,
                    date,
                    time,
                    aircraft_callsign,
                    aircraft_type,
                    aircraft_registration,
                    operator,
                    route,
                    event_description,
                    category,
                    cause,
                  }, index) => (
                    <tr key={id} className={`hover:bg-gray-750 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-750'}`}>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-sm text-gray-400" />
                            <span className="text-sm font-medium text-white">{date}</span>
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <FontAwesomeIcon icon={faClock} className="text-sm text-gray-400" />
                            <span className="text-xs text-gray-300">{time}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center space-x-2">
                            <FontAwesomeIcon icon={faPlane} className="text-sm text-gray-400" />
                            <span className="text-sm font-medium text-white">{aircraft_callsign}</span>
                          </div>
                          <div className="text-xs text-gray-300 mt-1">
                            <div>
                              {aircraft_type}
                              {' '}
                              -
                              {' '}
                              {aircraft_registration}
                            </div>
                            <div className="text-gray-400">{operator}</div>
                          </div>
                          {route && (
                            <div className="flex items-center space-x-2 mt-1">
                              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs text-gray-400" />
                              <span className="text-xs text-gray-300">{route}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="text-sm text-gray-300 max-w-xs">
                          {event_description}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-900 text-blue-200">
                          {category}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-900 text-yellow-200">
                          {cause}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Enhanced Pagination */}
              <div className="px-6 py-4 bg-gray-750 border-t border-gray-600">
                <Pagination
                  className="pagination-bar"
                  currentPage={currentPage}
                  totalCount={filteredLogs?.length || 0}
                  pageSize={PageSize}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <FontAwesomeIcon icon={faFileAlt} className="text-2xl text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-white mb-2">No safety logs found</h3>
              <p className="text-gray-400 mb-6">Try adjusting your search criteria</p>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
          {/* Safety Logs Statistics */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FontAwesomeIcon icon={faChartLine} className="mr-2 text-green-400" />
              Safety Logs Statistics
            </h3>
            <div className="space-y-4">
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">Total Logs</span>
                  <span className="text-white font-semibold">{totalLogs}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">This Month</span>
                  <span className="text-green-400 font-semibold">{thisMonthLogs}</span>
                </div>
              </div>
              <div className="bg-gray-750 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-300 text-sm">This Year</span>
                  <span className="text-blue-400 font-semibold">{thisYearLogs}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Top Categories */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Top Categories</h3>
            <div className="space-y-3">
              {topCategories.map(([category, count]) => (
                <div key={category} className="bg-gray-750 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 text-sm truncate">{category}</span>
                    <span className="text-green-400 font-semibold">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <LinkButton
                path="/private/safetylogs-form"
                linktext="Report New Log"
              />
              <button
                type="button"
                className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center space-x-2"
              >
                <FontAwesomeIcon icon={faDownload} className="text-sm" />
                <span>Export Data</span>
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
            <div className="space-y-3">
              {logs && logs.length > 0 ? (
                logs.slice(0, 3).map((log) => (
                  <div key={log.id} className="bg-gray-750 rounded-lg p-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-white truncate">{log.aircraft_callsign}</h4>
                        <p className="text-xs text-gray-400 mt-1">{log.date}</p>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        <span className="text-xs text-gray-500">{log.category}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-gray-750 rounded-lg p-3">
                  <p className="text-xs text-gray-400">No recent activity</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SafetyLogs;
